import axios from 'axios';

import { replaceWeeglooCdnUrl } from '@/api/zepetoful.api/zepetoful.api';
import { isLocal, isProduction } from '@/consts/env';

import type { CmsContentResponse } from './types';

const getCmsBaseUrl = () => {
  if (isProduction) return 'https://zep.us/cms';
  if (isLocal) return '/cms'; // Next.js rewrites로 프록시 (CORS 우회)
  return 'https://dev-3.zep.us/cms';
};

const CMS_BASE_URL = getCmsBaseUrl();

/**
 * zep-cms S3 기반 CMS 클라이언트
 * CDN URL: PRD → https://zep.us/cms, LOC → /cms (프록시), 그 외 → https://dev-3.zep.us/cms
 */
const CMS_INSTANCE = axios.create({
  baseURL: CMS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

/**
 * Response interceptor
 * - isProduction에 따라 json 또는 jsonDev 자동 선택
 * - JSON 문자열을 파싱 후 Weegloo CDN URL을 자체 프록시로 치환
 */
CMS_INSTANCE.interceptors.response.use(
  response => {
    const data = response.data as CmsContentResponse;

    // isProduction에 따라 json/jsonDev 선택
    const jsonString = isProduction ? data.json : data.jsonDev;

    // json이 없으면 jsonDev로 fallback
    const finalJsonString = jsonString ?? data.jsonDev ?? data.json;

    // JSON 문자열 파싱 후 weegloo CDN host 치환
    try {
      response.data = replaceWeeglooCdnUrl(JSON.parse(finalJsonString));
    } catch {
      console.error('[CMS] JSON 파싱 실패');
      throw new Error('CMS 콘텐츠 파싱 실패');
    }

    return response;
  },
  error => {
    console.error('[CMS] API 에러:', error);
    return Promise.reject(error);
  },
);

/**
 * CMS 콘텐츠 조회 fetcher
 * @param params.contentId - 콘텐츠 ID
 * @returns 파싱된 콘텐츠 데이터
 */
export const getCmsContent = async <T = CmsContentResponse>({
  contentId,
}: {
  contentId: string;
}): Promise<T> => {
  const response = await CMS_INSTANCE.get<T>(`/${contentId}`);
  return response.data;
};

export { CMS_INSTANCE };
